import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { FloatingLabelDatePicker } from "@/ui/components/FloatingLabelDatePicker";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { OptionChipGroup } from "@/ui/components/OptionChipGroup";
import {
  DEATH_CAUSE_OPTIONS,
  DEATH_CAUSE_REQUIRED_ERROR,
  DEATH_DATE_BEFORE_FOUND_ERROR,
  DEATH_DATE_FUTURE_ERROR,
  DEATH_DATE_INVALID_ERROR,
  DEATH_NOTE_REQUIRED_ERROR,
  type DeathCause,
} from "@/ui/care/closureDeceased";
import { useTheme } from "@/theme/ThemeProvider";
import { Text, View } from "react-native";

type Props = {
  cause: DeathCause | null;
  onCauseChange: (value: DeathCause | null) => void;
  note: string;
  onNoteChange: (value: string) => void;
  diedOn: string | null;
  onDiedOnChange: (value: string | null) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  foundOn: string | null;
  todayIso: string;
  showErrors: boolean;
  onValidityChange?: (valid: boolean) => void;
};

export function DeathClosureFields({
  cause,
  onCauseChange,
  note,
  onNoteChange,
  diedOn,
  onDiedOnChange,
  notes,
  onNotesChange,
  foundOn,
  todayIso,
  showErrors,
  onValidityChange,
}: Props) {
  const { theme } = useTheme();
  const causeMissing = showErrors && cause == null;
  const noteMissing = showErrors && cause === "other" && note.trim() === "";
  const found = foundOn?.trim().slice(0, 10) || null;

  return (
    <View style={{ gap: theme.space.sm }}>
      <Text style={{ color: theme.color.text, fontSize: theme.fontSize.small, fontWeight: "600" }}>
        Todesart
      </Text>
      <View accessibilityRole="radiogroup" accessibilityLabel="Todesart">
        <OptionChipGroup
          options={DEATH_CAUSE_OPTIONS}
          value={cause}
          allowDeselect={false}
          selection="radio"
          hitTargetMin={HIT_TARGET_MIN}
          onChange={(value) => {
            onCauseChange(value);
            if (value !== "other") onNoteChange("");
          }}
        />
      </View>
      {causeMissing ? (
        <View accessibilityRole="alert" accessibilityLiveRegion="assertive">
          <Text style={{ color: theme.color.dangerText, fontSize: theme.fontSize.tiny }}>
            {DEATH_CAUSE_REQUIRED_ERROR}
          </Text>
        </View>
      ) : null}
      {cause === "other" ? (
        <FloatingLabelInput
          label="Freitext"
          required
          value={note}
          onChangeText={onNoteChange}
          accessibilityLabel="Freitext zur Todesart"
          disableGlobalClickSync
        />
      ) : null}
      {noteMissing ? (
        <View accessibilityRole="alert" accessibilityLiveRegion="assertive">
          <Text style={{ color: theme.color.dangerText, fontSize: theme.fontSize.tiny }}>
            {DEATH_NOTE_REQUIRED_ERROR}
          </Text>
        </View>
      ) : null}
      <FloatingLabelDatePicker
        label="Todesdatum"
        value={diedOn}
        onChange={onDiedOnChange}
        optional
        allowClear
        maxIso={todayIso}
        maxIsoMessage={DEATH_DATE_FUTURE_ERROR}
        invalidIsoMessage={DEATH_DATE_INVALID_ERROR}
        minIso={found}
        minIsoMessage={DEATH_DATE_BEFORE_FOUND_ERROR}
        onValidityChange={onValidityChange}
        placeholder="Heute – oder entfernen"
      />
      <FloatingLabelInput
        label="Notiz"
        value={notes}
        onChangeText={onNotesChange}
        disableGlobalClickSync
      />
    </View>
  );
}
